import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Calculator, Check, X, Delete } from 'lucide-react';
import { createPortal } from 'react-dom';
import useModalBack from '../../hooks/useModalBack';
import { useConfig } from '../../context/ConfigContext';

/**
 * Calculator Input Component
 * Desktop: inline calculator preview
 * Mobile: Bottom sheet iOS-style calculator
 */
export default function CalculatorInput({
  name,
  value,
  onChange,
  placeholder = '0.00',
  className = '',
  error = false,
  icon: Icon = null,
  autoFocus = false
}) {
  const { config } = useConfig();
  const locale = config.currency === 'INR' ? 'en-IN' : 'en-US';

  const [expression, setExpression] = useState('');
  const [displayValue, setDisplayValue] = useState('');
  const [isCalculating, setIsCalculating] = useState(false);
  const [preview, setPreview] = useState(null);
  const [isMobile, setIsMobile] = useState(false);
  const [showCalcModal, setShowCalcModal] = useState(false);
  const [calcExpression, setCalcExpression] = useState('');
  const [calcJustEvaled, setCalcJustEvaled] = useState(false);
  const [calcHistory, setCalcHistory] = useState('');
  const inputRef = useRef(null);
  const displayRef = useRef(null);
  const closeCalcModal = useCallback(() => setShowCalcModal(false), []);

  useModalBack(showCalcModal, closeCalcModal, 'Calculator');

  useEffect(() => {
    if (autoFocus && inputRef.current) {
      const isMobileDevice = window.matchMedia('(max-width: 1279px)').matches || 'ontouchstart' in window;
      if (isMobileDevice) {
        const timer = setTimeout(() => inputRef.current?.focus(), 350);
        return () => clearTimeout(timer);
      }
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.matchMedia('(max-width: 1279px)').matches || 'ontouchstart' in window);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    if (!isCalculating) {
      const rawVal = value !== null && value !== undefined && value !== '' ? value.toString() : '';
      setExpression(rawVal);
      setDisplayValue(rawVal);
      setCalcExpression(rawVal);
    }
  }, [value, isCalculating]);

  useEffect(() => {
    if (isCalculating && expression) {
      setPreview(evaluateExpression(expression));
    } else {
      setPreview(null);
    }
  }, [expression, isCalculating]);

  const evaluateExpression = (expr) => {
    try {
      const cleanExpr = expr.replace(/\s/g, '');
      if (!/^[\d.+\-*/()]+$/.test(cleanExpr)) return null;
      if (!cleanExpr || /[+\-*/]{2,}/.test(cleanExpr)) return null;
      let parenCount = 0;
      for (const char of cleanExpr) {
        if (char === '(') parenCount++;
        if (char === ')') parenCount--;
        if (parenCount < 0) return null;
      }
      if (parenCount !== 0) return null;
      // eslint-disable-next-line no-new-func
      const result = new Function(`return (${cleanExpr})`)();
      if (typeof result === 'number' && !isNaN(result) && isFinite(result)) {
        return Math.round(result * 100) / 100;
      }
      return null;
    } catch {
      return null;
    }
  };

  const hasOperators = (str) => /[+\-*/]/.test(str);

  const validateNumberInput = (input) => {
    if (!input) return '';
    let cleaned = input.toString().replace(/[^\d.]/g, '');
    const parts = cleaned.split('.');
    if (parts.length > 2) cleaned = parts[0] + '.' + parts.slice(1).join('');
    if (parts.length === 2 && parts[1].length > 2) cleaned = parts[0] + '.' + parts[1].slice(0, 2);
    return cleaned;
  };

  const handleInputChange = (e) => {
    const inputValue = e.target.value;
    if (hasOperators(inputValue)) {
      setExpression(inputValue);
      setDisplayValue(inputValue);
      setIsCalculating(true);
    } else {
      const validatedNum = validateNumberInput(inputValue);
      setExpression(validatedNum);
      setDisplayValue(validatedNum);
      setIsCalculating(false);
      onChange({ target: { name, value: validatedNum, type: 'number' } });
    }
  };

  const handleBlur = () => {
    if (isCalculating && preview !== null) applyCalculation();
    else if (isCalculating) cancelCalculation();
  };

  const applyCalculation = () => {
    if (preview !== null) {
      const resultStr = preview.toString();
      setExpression(resultStr);
      setDisplayValue(resultStr);
      setIsCalculating(false);
      onChange({ target: { name, value: preview.toString(), type: 'number' } });
    }
  };

  const cancelCalculation = () => {
    const valStr = value ? value.toString() : '';
    setExpression(valStr);
    setDisplayValue(valStr);
    setIsCalculating(false);
    setPreview(null);
  };

  const handleKeyDown = (e) => {
    if (isCalculating) {
      if (e.key === 'Enter' || e.key === '=') { e.preventDefault(); applyCalculation(); }
      else if (e.key === 'Escape') { e.preventDefault(); cancelCalculation(); }
    }
  };

  // Format for locale display
  const formatNum = (val) => {
    const num = parseFloat(val);
    if (isNaN(num)) return val || '0';
    return num.toLocaleString(locale);
  };

  // Format expression for display: replace * with × and / with ÷
  const formatExprDisplay = (expr) => {
    if (!expr) return '0';
    return expr.replace(/\*/g, ' × ').replace(/\//g, ' ÷ ').replace(/\+/g, ' + ').replace(/-/g, ' − ');
  };

  // Quick amounts
  const quickAmounts = useMemo(() => {
    if (config.currency === 'INR') return [100, 500, 1000, 2000, 5000];
    if (config.currency === 'USD' || config.currency === 'EUR' || config.currency === 'GBP') return [10, 50, 100, 500, 1000];
    return [100, 500, 1000, 2000, 5000];
  }, [config.currency]);

  const formatQuickAmt = (amt) => amt >= 1000 ? `${amt / 1000}K` : amt.toString();

  // Open calculator
  const openCalculator = () => {
    setCalcExpression(expression || '');
    setCalcJustEvaled(false);
    setCalcHistory('');
    setShowCalcModal(true);
  };

  // Calculator button handler
  const handleCalcButton = (val) => {
    if (val === 'AC') {
      setCalcExpression('');
      setCalcHistory('');
      setCalcJustEvaled(false);
    } else if (val === '⌫') {
      if (calcJustEvaled) return;
      setCalcExpression(calcExpression.slice(0, -1));
    } else if (['+', '-', '×', '÷'].includes(val)) {
      const opMap = { '×': '*', '÷': '/' };
      if (calcJustEvaled) {
        setCalcHistory('');
        setCalcJustEvaled(false);
      }
      setCalcExpression(calcExpression + (opMap[val] || val));
    } else if (val === '.') {
      if (calcJustEvaled) {
        setCalcExpression('0.');
        setCalcHistory('');
        setCalcJustEvaled(false);
        return;
      }
      const lastOpIndex = Math.max(
        calcExpression.lastIndexOf('+'), calcExpression.lastIndexOf('-'),
        calcExpression.lastIndexOf('*'), calcExpression.lastIndexOf('/')
      );
      const currentNumber = calcExpression.slice(lastOpIndex + 1);
      if (!currentNumber.includes('.')) setCalcExpression(calcExpression + val);
    } else if (val === '00') {
      if (calcJustEvaled) {
        setCalcExpression('');
        setCalcHistory('');
        setCalcJustEvaled(false);
        return;
      }
      if (calcExpression.length > 0) setCalcExpression(calcExpression + '00');
    } else {
      if (calcJustEvaled) {
        setCalcExpression(val);
        setCalcHistory('');
        setCalcJustEvaled(false);
        return;
      }
      const lastOpIndex = Math.max(
        calcExpression.lastIndexOf('+'), calcExpression.lastIndexOf('-'),
        calcExpression.lastIndexOf('*'), calcExpression.lastIndexOf('/')
      );
      const currentNumber = calcExpression.slice(lastOpIndex + 1);
      const dotIndex = currentNumber.indexOf('.');
      if (dotIndex !== -1 && currentNumber.slice(dotIndex + 1).length >= 2) return;
      setCalcExpression(calcExpression + val);
    }
  };

  // Handle equals
  const handleCalcEquals = () => {
    if (!calcExpression) return;
    const result = evaluateExpression(calcExpression);
    if (result !== null) {
      setCalcHistory(formatExprDisplay(calcExpression) + ' =');
      setCalcExpression(result.toString());
      setCalcJustEvaled(true);
    }
  };

  // Handle quick amount
  const handleQuickAmount = (amt) => {
    setCalcExpression(String(amt));
    setCalcHistory('');
    setCalcJustEvaled(true);
  };

  // Apply and close
  const handleCalcDone = () => {
    const result = evaluateExpression(calcExpression);
    let finalValue;
    if (result !== null) finalValue = (Math.round(result * 100) / 100).toString();
    else finalValue = validateNumberInput(calcExpression);

    setExpression(finalValue);
    setDisplayValue(finalValue);
    setIsCalculating(false);
    onChange({ target: { name, value: finalValue, type: 'number' } });
    setShowCalcModal(false);
  };

  // What to show in display
  const calcPreview = hasOperators(calcExpression) ? evaluateExpression(calcExpression) : null;
  const calcDisplayNum = parseFloat(calcExpression) || 0;

  // Dynamic font size
  const getDisplayFontSize = () => {
    const str = calcExpression || '0';
    if (str.length > 14) return 'text-2xl';
    if (str.length > 10) return 'text-3xl';
    if (str.length > 7) return 'text-4xl';
    return 'text-5xl';
  };

  // Auto-scroll display to show latest (rightmost) content
  useEffect(() => {
    if (displayRef.current) {
      displayRef.current.scrollLeft = displayRef.current.scrollWidth;
    }
  }, [calcExpression]);

  // Button grid
  const buttons = [
    { label: 'AC', type: 'fn' }, { label: '÷', type: 'op' }, { label: '×', type: 'op' }, { label: '⌫', type: 'fn' },
    { label: '7', type: 'num' }, { label: '8', type: 'num' }, { label: '9', type: 'num' }, { label: '-', type: 'op' },
    { label: '4', type: 'num' }, { label: '5', type: 'num' }, { label: '6', type: 'num' }, { label: '+', type: 'op' },
    { label: '1', type: 'num' }, { label: '2', type: 'num' }, { label: '3', type: 'num' }, { label: '=', type: 'eq' },
    { label: '00', type: 'num' }, { label: '0', type: 'num' }, { label: '.', type: 'num' }, { label: 'Save', type: 'done' },
  ];

  const getButtonClasses = (type) => {
    const base = 'h-[56px] rounded-2xl font-medium transition-all active:scale-95 flex items-center justify-center select-none';
    switch (type) {
      case 'num': return `${base} bg-white text-surface-900 text-xl shadow-sm active:bg-surface-50`;
      case 'fn': return `${base} bg-surface-200 text-surface-700 text-lg active:bg-surface-300`;
      case 'op': return `${base} text-xl font-semibold active:opacity-80`;
      case 'eq': return `${base} text-white font-bold text-2xl shadow-md active:opacity-80`;
      case 'done': return `${base} text-white font-bold text-sm shadow-md active:opacity-80`;
      default: return base;
    }
  };

  const getButtonStyle = (type) => {
    if (type === 'op') return { backgroundColor: 'color-mix(in srgb, var(--accent-bg) 15%, white)', color: 'var(--accent-bg)' };
    if (type === 'eq') return { backgroundColor: 'var(--accent-bg)' };
    if (type === 'done') return { background: 'linear-gradient(135deg, var(--accent-bg), color-mix(in srgb, var(--accent-bg) 80%, black))' };
    return {};
  };

  return (
    <div className="relative">
      <div className="relative">
        {Icon && (
          <Icon className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-surface-400" />
        )}
        <input
          ref={inputRef}
          type="text"
          inputMode="decimal"
          name={name}
          value={displayValue}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onBlur={handleBlur}
          placeholder={placeholder}
          autoFocus={autoFocus && !isMobile}
          className={`${className} ${Icon ? 'pl-10' : ''} ${error ? 'input-error' : ''} ${isCalculating && !isMobile ? 'pr-32' : 'pr-12'}`}
        />

        {/* Desktop: Calculator indicator and preview */}
        {isCalculating && !isMobile && (
          <div className="absolute right-12 top-1/2 -translate-y-1/2 flex items-center gap-1">
            {preview !== null ? (
              <>
                <span className="text-sm text-surface-500 bg-surface-100 px-2 py-0.5 rounded">= {preview}</span>
                <button type="button" onClick={applyCalculation} className="p-1 text-income-600 hover:bg-income-50 rounded" title="Apply (Enter)">
                  <Check className="w-4 h-4" />
                </button>
                <button type="button" onClick={cancelCalculation} className="p-1 text-expense-600 hover:bg-expense-50 rounded" title="Cancel (Esc)">
                  <X className="w-4 h-4" />
                </button>
              </>
            ) : (
              <span className="text-xs text-surface-400">Type +, -, *, /</span>
            )}
          </div>
        )}

        <button
          type="button"
          onClick={openCalculator}
          className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 text-primary-600 bg-primary-50 hover:bg-primary-100 rounded-lg transition-colors"
          title="Open calculator"
        >
          <Calculator className="w-5 h-5" />
        </button>
      </div>

      {/* Calculator Modal */}
      {showCalcModal && createPortal(
        <div
          className="fixed inset-0 bg-surface-900/60 backdrop-blur-sm flex items-end justify-center z-[100] md:items-center md:p-4"
          onClick={() => setShowCalcModal(false)}
        >
          <div
            className="bg-surface-100 rounded-t-2xl md:rounded-2xl shadow-2xl w-full md:max-w-sm flex flex-col overflow-hidden animate-slide-up max-h-[95vh]"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Drag handle */}
            <div className="flex justify-center pt-2 pb-0 md:hidden">
              <div className="w-10 h-1 rounded-full bg-surface-300" />
            </div>

            {/* Header */}
            <div className="flex items-center justify-between px-4 py-2.5 bg-white border-b border-surface-100">
              <span className="text-sm font-semibold text-surface-900">Calculator</span>
              <button
                type="button"
                onClick={() => setShowCalcModal(false)}
                className="p-1.5 text-surface-400 hover:text-surface-600 hover:bg-surface-100 rounded-lg transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Display area */}
            <div className="bg-white px-5 pt-2 pb-4 flex flex-col justify-end min-h-[90px]">
              {/* History / previous expression */}
              <div className="text-right text-sm text-surface-400 mb-1 truncate min-h-[20px]">
                {calcHistory}
              </div>

              {/* Main display — shows expression while typing, result after = */}
              <div ref={displayRef} className="overflow-x-auto" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none', WebkitOverflowScrolling: 'touch' }}>
                <div className={`font-bold text-surface-900 leading-tight whitespace-nowrap text-right min-w-fit ${getDisplayFontSize()}`}>
                  {calcExpression ? formatExprDisplay(calcExpression) : '0'}
                </div>
              </div>

              {/* Live result preview when expression has operators */}
              {calcPreview !== null && !calcJustEvaled && (
                <div className="text-right text-base text-surface-400 mt-1">
                  = {formatNum(calcPreview.toString())}
                </div>
              )}
            </div>

            {/* Calculator buttons */}
            <div className="grid grid-cols-4 gap-[6px] p-3 bg-surface-100">
              {buttons.map((btn, i) => (
                <button
                  key={i}
                  type="button"
                  onClick={() => {
                    if (btn.label === '=') handleCalcEquals();
                    else if (btn.label === 'Save') handleCalcDone();
                    else handleCalcButton(btn.label);
                  }}
                  className={getButtonClasses(btn.type)}
                  style={getButtonStyle(btn.type)}
                >
                  {btn.label === '⌫' ? <Delete className="w-5 h-5" /> : btn.label}
                </button>
              ))}
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}
